import React, { useContext, useEffect } from 'react';

import { Controller } from 'react-hook-form';
import { Calendar } from 'primereact/calendar';

import InputErrorMessage from 'components/UI/InputErrorMessage/InputErrorMessage';
import LabelName from 'components/UI/LabelName/LabelName';

import { useCalendarContext } from 'modules/calendar/Context/CalendarContext';
import moment from 'moment-timezone';
import { UserDetailsContext, useUserDetailsContext } from 'context/userDetailsContext';
import { roundToNextHalfHour } from 'utils/utility_functions/timezone';

function EventDate({ control, trigger, errors, setValue, timezoneBasedTime, watch }) {
  const { editEventModal, calendarDayClickedDetails } = useCalendarContext();
  const userContext = useContext(UserDetailsContext);
  const { userDetails } = useUserDetailsContext();
  const recurrenceLimitWatcher = watch('recurrence_keys.recurrence_limit');
  const isRecurringWatcher = watch('is_recurring');
  const allDay = watch('all_day'); // watch All Day toggle

  function changeTimeValues(time) {
    if (time) {
      let startTime = roundToNextHalfHour(new Date(time));
      setValue('meeting_start_time', startTime);
      let updatedTime = roundToNextHalfHour(time);
      let addedTime = updatedTime.setMinutes(updatedTime.getMinutes() + 30);
      let endTime = new Date(addedTime);
      setValue('meeting_end_time', endTime);

      if (isRecurringWatcher && startTime > new Date(recurrenceLimitWatcher)) {
        setValue('recurrence_keys.recurrence_limit', startTime);
      }
      //   if(recurrenceLimitWatcher) {
    }
  }

  useEffect(() => {
    if (!editEventModal) {
      setValue('when', new Date(timezoneBasedTime));
    }
  }, []);

  useEffect(() => {
    if (!editEventModal && calendarDayClickedDetails?.dateStr) {
      const localMoment = moment(calendarDayClickedDetails?.dateStr, 'YYYY-MM-DDTHH:mm:ss');
      const dateObj = localMoment.toDate();
      setValue('when', dateObj);
      changeTimeValues(dateObj);
    }
  }, [calendarDayClickedDetails?.date, editEventModal, userContext?.userDetails?.timezone]);

  // keep Till date sane & clear it when All Day is turned off
  useEffect(() => {
    if (!allDay) setValue('till', null);
    else if (!watch('till')) setValue('till', watch('when'));
  }, [allDay]);

  return (
    <div className="d-flex align-items-center py-2 flex-wrap">
      {/* Label */}
      <div className="col-md-4 col-12">
        <LabelName required={true} htmlFor="when">
          Date
        </LabelName>
      </div>

      {/* Fields */}
      <div className="col-md-8 col-12 d-flex justify-content-between align-items-start">
        {/* Start Date ("When") */}
        <div className={allDay ? 'w-50 pe-2' : 'w-100'}>
          <Controller
            name="when"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <Calendar
                id={field.name}
                value={moment(field.value)?.tz(userDetails?.timezone)?.toDate()}
                onChange={(e) => {
                  field.onChange(e.value);
                  trigger('when');
                  // if All Day is on, keep Till >= When
                  if (allDay) {
                    const till = watch('till');
                    if (!till || new Date(e.value) > new Date(till)) {
                      setValue('till', e.value);
                      trigger('till');
                    }
                  }
                }}
                minDate={new Date(timezoneBasedTime)}
                placeholder="When"
                monthNavigator
                yearNavigator
                yearRange="1950:2050"
                className="input-shadow w-100"
                inputRef={field.ref}
              />
            )}
          />
          {errors?.when?.type === 'required' && <InputErrorMessage className="mt-1">Please select event date</InputErrorMessage>}
        </div>

        {/* End Date ("Till") – only if All Day checked */}
        {allDay && (
          <div className="w-50">
            <Controller
              name="till"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Calendar
                  id={field.name}
                  value={moment(field.value)?.tz(userDetails?.timezone)?.toDate()}
                  onChange={(e) => {
                    field.onChange(e.value);
                    trigger('till');
                  }}
                  minDate={watch('when') || new Date(timezoneBasedTime)}
                  placeholder="Till"
                  monthNavigator
                  yearNavigator
                  yearRange="1950:2050"
                  className="input-shadow w-100"
                  inputRef={field.ref}
                />
              )}
            />
            {allDay && errors?.till?.type === 'required' && <InputErrorMessage className="mt-1">Please select end date</InputErrorMessage>}
          </div>
        )}
      </div>
    </div>
  );
}

export default EventDate;
